/**
 * samTTS.js — Single voice TTS interface for SamSurveyor
 *
 * All apps (Designer, Help Agent, etc.) use this same module to speak
 * via the kernel's Google Cloud TTS endpoint: POST /api/tts/speak
 *
 * Usage:
 *   import { samTTS } from './samTTS';
 *   await samTTS.speak('Hello!', { voice: 'en-US-Chirp3-HD-Achernar' });
 *   samTTS.stop();
 */

const DEFAULT_VOICE = 'en-US-Chirp3-HD-Achernar';

let _currentAudio = null;
let _kernelApiBase = null;
let _warmPromise = null;
let _playbackGeneration = 0;
let _finishCurrentAudio = null;

function disposePrepared(prepared) {
    if (!prepared || prepared.disposed) return;
    prepared.disposed = true;
    URL.revokeObjectURL(prepared.url);
}

/**
 * Resolve the kernel API base URL.
 * Checks env var first, then falls back to localhost / production URL.
 */
function getKernelApiBase() {
    if (_kernelApiBase) return _kernelApiBase;

    // React build-time env
    if (typeof process !== 'undefined' && process.env?.REACT_APP_KERNEL_API_BASE) {
        return process.env.REACT_APP_KERNEL_API_BASE;
    }

    if (typeof window !== 'undefined') {
        const h = window.location.hostname;
        if (h === 'localhost' || h === '127.0.0.1') return 'http://localhost:3000/api';
    }

    // Production Cloud Run URL
    return 'https://cloudsam-kernel-vm77772tsa-uc.a.run.app/api';
}

/**
 * Speak text via Google Cloud TTS through the kernel.
 * Returns a promise that resolves when audio finishes.
 *
 * @param {string} text       — Text to speak
 * @param {object} [options]  — { voice, language, context, volume }
 */
async function prepare(text, options = {}) {
    if (!text || !text.trim()) return;

    const base = getKernelApiBase();
    const voice = options.voice || DEFAULT_VOICE;

    try {
        const res = await fetch(`${base}/tts/speak`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: options.signal,
            body: JSON.stringify({
                text: text.trim(),
                voice,
                language: options.language,
                context: options.context || 'sam_tts',
            }),
        });

        if (!res.ok) {
            console.warn('[samTTS] Kernel responded with', res.status);
            return;
        }

        const data = await res.json();
        if (!data.success || !data.audioData) {
            console.warn('[samTTS] No audio data in response');
            return;
        }

        // Decode base64 → blob → Audio
        const binary = atob(data.audioData);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        const mime = data.audioFormat === 'ogg_opus' ? 'audio/ogg' : 'audio/mp3';
        const blob = new Blob([bytes], { type: mime });
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        return { audio, url, disposed: false };
    } catch (e) {
        if (e?.name !== 'AbortError') console.warn('[samTTS] Error:', e);
    }
}

/** Play audio prepared by prepare(). Only one Sam audio element plays at once. */
async function play(prepared, options = {}) {
    if (!prepared || prepared.disposed) return false;
    stop();
    const playbackGeneration = _playbackGeneration;
    const { audio } = prepared;
    try {
        _currentAudio = audio;
        audio.volume = options.volume ?? 0.8;

        return new Promise((resolve) => {
            const finish = (result) => {
                disposePrepared(prepared);
                if (_currentAudio === audio) _currentAudio = null;
                if (_finishCurrentAudio === finish) _finishCurrentAudio = null;
                resolve(result);
            };
            _finishCurrentAudio = finish;
            audio.onended = () => finish(playbackGeneration === _playbackGeneration);
            audio.onerror = () => finish(false);
            audio.play().catch(() => {
                finish(false);
            });
        });
    } catch (e) {
        disposePrepared(prepared);
        console.warn('[samTTS] Playback error:', e);
        return false;
    }
}

async function speak(text, options = {}) {
    stop();
    const playbackGeneration = _playbackGeneration;
    const prepared = await prepare(text, options);
    if (playbackGeneration !== _playbackGeneration) {
        disposePrepared(prepared);
        return false;
    }
    return play(prepared, options);
}

/**
 * Initialise the Kernel's lazy TTS client before the first utterance. Status
 * is deliberately best-effort: audio generation remains the source of truth.
 */
function warm() {
    if (_warmPromise) return _warmPromise;
    const base = getKernelApiBase();
    _warmPromise = fetch(`${base}/tts/status`)
        .then(res => res.ok ? res.json() : null)
        .catch(() => null);
    return _warmPromise;
}

/**
 * Stop any in-progress audio immediately.
 */
function stop() {
    _playbackGeneration += 1;
    if (_currentAudio) {
        try {
            _currentAudio.pause();
            _currentAudio.currentTime = 0;
            _currentAudio.onended = null;
            _currentAudio.onerror = null;
        } catch (_) { }
        _currentAudio = null;
    }
    if (_finishCurrentAudio) _finishCurrentAudio(false);
}

/**
 * Check if audio is currently playing.
 */
function isSpeaking() {
    return _currentAudio !== null && !_currentAudio.paused;
}

/**
 * Override the kernel API base URL (for tests or custom configs).
 */
function setKernelApiBase(url) {
    _kernelApiBase = url;
}

export const samTTS = { speak, prepare, play, dispose: disposePrepared, warm, stop, isSpeaking, setKernelApiBase, DEFAULT_VOICE };
export default samTTS;
